import React from 'react';
import { notificationColors, colors, fontWeight } from '@mergermarket/acuris-react-styles';
import styled from 'styled-components';

const transitionFirstPadding = '30ms';
const transitionLastPadding = transitionFirstPadding;
const transitionWidth = '240ms';
const delayWidth = transitionFirstPadding;
const finalAnimationDelay = '270ms';
const stressedColors = {
  STRESSED_WATCHLIST: notificationColors.warning,
  DISTRESSED_WATCHLIST: notificationColors.failure,
  LIVE_RESTRUCTURING_WATCHLIST: notificationColors.failureDarker,
  POST_RESTRUCTURING: notificationColors.successDarker
};

const applyHoverState = isInverse => isInverse ? hoverInverseStyles : hoverStyles;

const hoverInverseStyles = `
  width: 0;
  padding: 0;
  border-left: 0 solid transparent;
  transition: padding-left ${transitionFirstPadding} linear ${finalAnimationDelay},
    border-left ${transitionFirstPadding} linear ${finalAnimationDelay},
    width ${transitionWidth} linear ${delayWidth},
    padding-right ${transitionLastPadding} linear 0ms;
`;
const hoverStyles = `
  width: 100%;
  padding: 0 0.5rem;
  border-left: 4px solid ${colors.primaryFont};
  transition: padding-left ${transitionFirstPadding} linear 0ms,
    border-left ${transitionFirstPadding} linear 0ms,
    width ${transitionWidth} linear ${delayWidth},
    padding-right ${transitionLastPadding} linear ${finalAnimationDelay};
`;
const focusStyle = `
  outline: solid 1px ${colors.outline};
`;
const situationLabelSharedStyles = `
  display: inline-block;
  height: 18px;
  font-weight: ${fontWeight.bold};
  padding: 0;
  border: none;
  position: relative;
  margin-top: 0.4rem;
  font-size: 1.2rem;
  line-height: 1.8;
  outline: 0;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;

  &:hover{
    ${props => applyHoverState(props.isInverse)}
  }

  &:focus{
    ${focusStyle}
  }
`;
const StyledSituationLabel = styled.button`
  cursor: pointer;
  ${situationLabelSharedStyles}
`;
const StyledSituationLabelLink = styled.a`
  cursor: pointer;
  ${situationLabelSharedStyles}
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;
const StyledSituationLabelInactive = styled.span`
  ${situationLabelSharedStyles};
`;
const StyledSituationLabelText = styled.div`
  background-color: ${props => props.isInactive ? 'transparent' : colors.primaryBackground};
  padding: 0 0.5rem;
  text-align: left;
  font-weight: ${fontWeight.bold};
  font-size: 1rem;
  color: ${colors.primaryFont};
  border-left: 0.4rem solid;
  border-color: ${props => props.isInactive ? colors.primaryFont : stressedColors[props.stressedState]};
`;
const StyledSituationLabelTextInverse = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 0;
  overflow: hidden;
  word-break: keep-all;
  text-overflow: clip;
  color: ${colors.primaryBackground};
  padding: 0;
  border-left: 0 solid transparent;
  font-size: 1rem;
  text-align: left;
  white-space: nowrap;
  background-color: ${props => stressedColors[props.stressedState]};

  transition: padding-right ${transitionFirstPadding} linear 0ms, width ${transitionWidth} linear ${delayWidth},
    padding-left ${transitionLastPadding} linear ${finalAnimationDelay},
    border-left ${transitionLastPadding} linear ${finalAnimationDelay};

  ${StyledSituationLabel}:hover &,
  ${StyledSituationLabelLink}:hover & {
    ${props => applyHoverState(props.isInverse)}
  }

  ${StyledSituationLabel}:focus &,
  ${StyledSituationLabelLink}:focus & {
    ${focusStyle}
  }

  ${props => props.isInverse && applyHoverState()}
`;

const SITUATION_LABEL_DISPLAY_NAME = {
  STRESSED_WATCHLIST: 'Stressed Watchlist',
  DISTRESSED_WATCHLIST: 'Distressed Watchlist',
  LIVE_RESTRUCTURING_WATCHLIST: 'Restructuring Watchlist',
  POST_RESTRUCTURING: 'Post Restructuring'
};

const SituationLabelContent = ({
  stressedState,
  isInverse,
  isInactive
}) => {
  const text = SITUATION_LABEL_DISPLAY_NAME[stressedState];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledSituationLabelText, {
    stressedState: stressedState,
    isInverse: isInverse,
    isInactive: isInactive,
    "data-testid": "SituationLabelVisibleText",
    "data-test-situation-label-visible-text": true
  }, text), !isInactive && /*#__PURE__*/React.createElement(StyledSituationLabelTextInverse, {
    "aria-hidden": true,
    stressedState: stressedState,
    isInverse: isInverse,
    "data-testid": "SituationLabelText",
    "data-test-situation-label-text": true
  }, text));
};

const SituationLabel = ({
  stressedState,
  url,
  onClick,
  className,
  isInverse,
  isInactive,
  id
}) => {
  if (isInactive) {
    return /*#__PURE__*/React.createElement(StyledSituationLabelInactive, {
      className: className,
      "data-testid": "SituationLabelInactive",
      "data-situation-label-inactive": true
    }, /*#__PURE__*/React.createElement(SituationLabelContent, {
      stressedState: stressedState,
      isInverse: isInverse,
      isInactive: true
    }));
  }

  if (url) {
    return /*#__PURE__*/React.createElement(StyledSituationLabelLink, {
      className: className,
      href: url,
      onClick: ev => {
        typeof onClick === 'function' && onClick(ev, id);
      },
      "data-testid": "SituationLabelLink",
      "data-situation-label-link": true
    }, /*#__PURE__*/React.createElement(SituationLabelContent, {
      stressedState: stressedState,
      isInverse: isInverse
    }));
  }

  return /*#__PURE__*/React.createElement(StyledSituationLabel, {
    className: className,
    onClick: ev => {
      typeof onClick === 'function' && onClick(ev, id);
    },
    "data-testid": "SituationLabelButton",
    "data-situation-label-button": true
  }, /*#__PURE__*/React.createElement(SituationLabelContent, {
    stressedState: stressedState,
    isInverse: isInverse
  }));
};

export default SituationLabel;
//# sourceMappingURL=index.js.map
